import { Form } from 'antd'
import { useState } from 'react'
import WizrInput from '../WizrInput'
import WizrFlexSpacing from '../WizrFlexSpacing'
import WizrButton from '../WizrButton'
import WizrFlexLayout from '../WizrFlexLayout'
import { styled } from 'styled-components'
import WizrText from '../WizrText'
import { useContentLoadingSelector } from '../../redux/selectors/content'
import { dispatchGetUserDetails, dispatchUpdateUserDetails } from '../../redux/actions/content'
import WizrDropDown from '../WizrDropDown'

const StyledContainer = styled(WizrFlexLayout)`
  gap: 8px;
`
const StyledWizrInput = styled(WizrInput)`
  min-width: 25vw;
`
const InputContainer = styled(WizrFlexLayout)`
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  background-color: #fff;
`
const StyledDropDown = styled(WizrDropDown)`
  height: 50px;
  .ant-select-selector {
    height: 50px !important;
    background-color: #f2f4f9 !important;
    .ant-select-selection-placeholder {
      margin-top: 10px;
    }
  }
`

interface IEditUserDetails {
  profileDetails?: { [key: string]: any }
  onCancel: () => void
}

const EditUserDetails = ({ profileDetails, onCancel }: IEditUserDetails): JSX.Element => {
  const loading = useContentLoadingSelector()
  const [form] = Form.useForm()

  // Error state variables for validation
  const [showNameValidation, setShowNameValidation] = useState<boolean>(false)
  const [showMobileValidation, setShowMobileValidation] = useState<boolean>(false)

  const [userDetails, setUserDetails] = useState(
    profileDetails ?? {
      id: '',
      displayName: '',
      mail: null,
      mobilePhone: [],
      createdDateTime: '',
      accountEnabled: null,
      extension_2422f8c615d444ee898955a9dd646513_UserRole: '',
    },
  )

  const setDetails = (value: any, key: any) => {
    setUserDetails({
      ...userDetails,
      [key]: value,
    })

    // Clear validation errors as user types
    switch (key) {
      case 'displayName':
        if (showNameValidation && value?.trim()) {
          setShowNameValidation(false)
        }
        break
      case 'mobilePhone':
        if (showMobileValidation && value && /^[6-9]\d{9}$/.test(value.toString())) {
          setShowMobileValidation(false)
        }
        break
    }
  }
  const OnFormSubmission = async () => {
    const isBlank = (text?: string) => !text || text.trim() === ''

    const validateInputs = (): boolean => {
      let isValid = true

      // Validate name
      if (isBlank(userDetails?.displayName)) {
        setShowNameValidation(true)
        isValid = false
      } else {
        setShowNameValidation(false)
      }

      // Validate mobile number if provided
      const mobileNumber = userDetails?.mobilePhone?.toString() || ''
      if (!mobileNumber.trim() || !/^[6-9]\d{9}$/.test(mobileNumber)) {
        setShowMobileValidation(true)
        isValid = false
      } else {
        setShowMobileValidation(false)
      }

      return isValid
    }

    if (!validateInputs()) {
      return
    }

    const payload = {
      user_id: userDetails?.id,
      user_data: {
        name: userDetails?.displayName,
        phone_no: userDetails?.mobilePhone,
        user_role: userDetails?.role,
      },
    }

    const success = await dispatchUpdateUserDetails(payload)

    if (success) {
      await dispatchGetUserDetails(userDetails?.id)
      onCancel()
    }
  }
  return (
    <StyledContainer background='#FFF' justifyContent='space-evenly'>
      <Form
        name='basic'
        form={form}
        initialValues={{ remember: true }}
        onFinish={() => {
          OnFormSubmission()
        }}
        onFinishFailed={() => null}
        autoComplete='off'
      >
        <Form.Item name='name'>
          <InputContainer>
            <WizrText type='body2'>Name</WizrText>
            <WizrInput
              placeholder='Enter Name '
              background={'#F2F4F9'}
              value={userDetails?.displayName}
              onChange={(value: any) => {
                setDetails(value, 'displayName')
              }}
              showError={showNameValidation}
              errorMessage='Please enter a valid name'
            />
          </InputContainer>
        </Form.Item>
        <Form.Item name='role'>
          <InputContainer>
            <WizrText type='body2'>Role</WizrText>
            <WizrInput
              placeholder='Role'
              background={'#F2F4F9'}
              value={userDetails?.role}
              disabled={true}
            ></WizrInput>
          </InputContainer>
        </Form.Item>

        <Form.Item name='mail' rules={[{ required: false, whitespace: true }]}>
          <InputContainer>
            <WizrText type='body2'>Email</WizrText>
            <WizrInput
              placeholder='Enter Email '
              background={'#F2F4F9'}
              value={userDetails?.mail ? userDetails?.mail : 'NA'}
              onChange={(value: any) => {
                setDetails(value, 'mail')
              }}
              disabled={true}
            ></WizrInput>
          </InputContainer>
        </Form.Item>
        <Form.Item name='phone' rules={[{ required: false, whitespace: true }]}>
          <InputContainer>
            <WizrText type='body2'>Phone Number</WizrText>
            <WizrInput
              placeholder='Enter Mobile Number '
              background={'#F2F4F9'}
              value={userDetails?.mobilePhone}
              onChange={(value: any) => {
                setDetails(value, 'mobilePhone')
              }}
              showError={showMobileValidation}
              errorMessage='Please enter a valid 10-digit mobile number starting with 6-9'
            />
          </InputContainer>
        </Form.Item>

        <Form.Item>
          <WizrFlexSpacing
            justifyContent='flex-end'
            flexDirection='row'
            size='large'
            background='#FFF'
          >
            <WizrButton
              type='default'
              text='Cancel'
              onClick={onCancel}
              style={{ borderRadius: '20px' }}
            />
            <WizrButton
              htmlType='submit'
              text='Save'
              disabled={loading}
              style={{ borderRadius: '20px' }}
            />
          </WizrFlexSpacing>
        </Form.Item>
      </Form>
    </StyledContainer>
  )
}

export default EditUserDetails
